import { z } from 'zod';

/**
 * Hipto Collect — `POST /collect/validate` schemas (inlined).
 *
 * Source-of-truth: `hipto-collect-module/src/landing-page/dto/validation-data.dto.ts`
 * + `validation-result.dto.ts`. We re-derive the minimal Zod shapes Doorly
 * needs to call the endpoint without pulling a hard dep on the collect repo.
 *
 * Endpoint is public (no auth, CORS open). For lead delivery from Doorly we
 * always send `stepType: 'DIRECT_SEND'` + `isFinalStep: true` so the LP flow
 * dispatches DALI + Manager immediately (no temporisation).
 */

export const StepTypeEnum = z.enum([
  'DIRECT_SEND',
  'TEMPORISATION',
  'TEMPORISATION_UPDATE',
  'TEMPORISATION_BYPASS_FORCE_DELIVERY',
]);
export type StepType = z.infer<typeof StepTypeEnum>;

/** Body of POST /collect/validate. */
export const CollectValidateRequestSchema = z.object({
  pageId: z.string().uuid(),
  stepId: z.string().optional(),
  stepType: StepTypeEnum.optional(),
  isFinalStep: z.boolean().optional(),
  userId: z.string().optional(),
  /** Free-form prospect data — keys must match what the LP flow expects. */
  collectedDatas: z.record(z.string(), z.unknown()).optional(),
  /**
   * Delay in minutes (string!) before Collect dispatches the lead. Only
   * meaningful when `stepType === 'TEMPORISATION'`. Default upstream is '10'.
   */
  temporisationTime: z.string().optional(),
  /**
   * Temporisation engine version. `v2` = new Postgres + Cloud Tasks engine
   * (current). `v1` = legacy Firebase CF flow, kept for fallback. Defaults
   * upstream to `v1` if omitted, so we always send `v2` explicitly when
   * we kick off a TEMPORISATION step.
   */
  temporisationVersion: z.enum(['v1', 'v2']).optional(),
  forcedSk: z.string().nullable().optional(),
  /**
   * Hopti `targetAccountId` — when the LP flow uses Hopti routing, this
   * forces dispatch to a specific Hopti account (bypasses prioritization).
   * Mirrors `forcedSk` for the Manager path.
   */
  forceAccountId: z.string().nullable().optional(),
  /**
   * Bypass des filtres/quotas partenaire côté Hopti. Feature
   * spécifiquement Hopti — n'a de sens que conjointement avec
   * `forceAccountId`. Doorly l'active systématiquement quand le
   * partenaire de livraison a un `hoptiAccountId` (le contexte DTC
   * justifie le push prioritaire au partenaire ; la régulation
   * volumique est gérée côté Doorly via `DeliveryPartner.dailyQuota`).
   */
  forceDelivery: z.boolean().optional(),
  userAgent: z.string().optional(),
});
export type CollectValidateRequest = z.infer<typeof CollectValidateRequestSchema>;

/**
 * DALI handshake — present on success. Loose-typed on purpose : Collect
 * sometimes returns `filterApplied: false` instead of a string, et le pipeline
 * DALI peut renvoyer `customer_id: null` pour certaines verticales (alarme
 * notamment) — on autorise donc null/optional sur les fields auxiliaires.
 * On ne marque la livraison comme failed que si la réponse est *vraiment*
 * cassée (pas de sk + pas de customer_id), pas pour un null ponctuel.
 */
export const DaliResponseSchema = z
  .object({
    sk: z.string().nullable().optional(),
    customer_id: z.string().nullable().optional(),
    hnos: z.boolean().optional(),
    opt_in: z.string().optional(),
    documentId: z.string().nullable().optional(),
    filterApplied: z.unknown().optional(),
  })
  .passthrough();
export type DaliResponse = z.infer<typeof DaliResponseSchema>;

const LocalizedErrorSchema = z
  .object({
    fr: z.string().optional(),
    en: z.string().optional(),
    es: z.string().optional(),
    it: z.string().optional(),
  })
  .passthrough();

/**
 * Response of POST /collect/validate. We're tolerant on optional fields
 * (`null` allowed everywhere they could legitimately be empty) so a
 * benign upstream change doesn't mark the delivery as failed.
 */
export const CollectValidateResponseSchema = z
  .object({
    success: z.boolean(),
    data: z
      .object({
        isValid: z.boolean().optional(),
        errors: z.array(LocalizedErrorSchema).optional(),
        validatedData: z.record(z.string(), z.unknown()).optional(),
        message: z.string().nullable().optional(),
        forceNextStepId: z.string().nullable().optional(),
      })
      .passthrough()
      .optional(),
    daliResponse: DaliResponseSchema.optional(),
  })
  .passthrough();
export type CollectValidateResponse = z.infer<typeof CollectValidateResponseSchema>;
